import { AriApiQueryCreatorFetch } from './AriApiCreator';
import { prismScopes } from './auth';
import getPrismBaseUrl from './AIMApi/controllers/utils/getBaseUrl';


// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Represents a record for a specific variety and division within an enterprise for a given crop year.
 *
 * @param cropYear - The year for which crop data is being retrieved.
 * @param enterprise - The enterprise associated with the crop.
 * @param variety - The variety of the crop; may be null if not specified.
 * @param division - The political division (e.g., state) for the crop; may be null if not specified.
 * @param directManaged - A flag indicating if the crop is directly managed (1 for yes, 0 for no).
 * @param plantedAcres - The number of acres planted; can be null if data is unavailable.
 * @param fieldCount - The total count of fields associated with this row.
 * @returns An object representing a single row of future crop data.
 */
export interface FutureCropRow {
  cropYear: number;
  enterprise: string;
  variety: string | null;
  division: string | null;
  directManaged: number;
  plantedAcres: number | null;
  fieldCount: number;
}

/** A single row from the GetTerraCrops endpoint (grouped by crop-year/enterprise/variety/division). */
export interface TerraCropRow {
  cropYear: number;
  enterpriseAbbr: string;
  enterpriseDescription: string;
  varietyAbbr: string | null;
  varietyDescription: string | null;
  businessUnit: string | null;
  yieldUOM: string | null;
  totalAcres: number | null;
  fieldCount: number;
  avgYield: number | null;
  minYield: number | null;
  maxYield: number | null;
  plantedAcres: number | null;
  plantedFieldCount: number;
  harvestedAcres: number | null;
  harvestedFieldCount: number;
}

/** Response shape from GetTerraCrops — keyed by crop year string. */
export type GetTerraCropsResponse = Record<string, TerraCropRow[]>;

/** Request shape for GetTerraCrops — supports optional Area filtering for cross-state-border accuracy. */
export interface GetTerraCropsRequest {
  businessUnits: string[];
  /** Optional area numbers to filter by (from farm-unit GIS layers). */
  areas?: number[];
}

// ─── Service ──────────────────────────────────────────────────────────────────

const serviceurl = getPrismBaseUrl();

/**
 * Normalize the query arg for `getTerraCrops` so that both the legacy
 * `string[]` shape and the new `GetTerraCropsRequest` object produce a
 * stable cache key and correct POST body.
 */
function normalizeTerraCropsArg(
  arg: string[] | GetTerraCropsRequest
): GetTerraCropsRequest {
  return Array.isArray(arg) ? { businessUnits: arg } : arg;
}

/**
 * The application's primary interface for interacting with the Terra API, providing query definitions for enterprise areas, future crop planning, and comprehensive crop reports.
 *
 * It provides a set of RTK Query hooks for interacting with the Terra API, specifically for retrieving enterprise acres, future crops, and general crop data across various identifiers.
 *
 * @param None - Not applicable as this is an exported constant object.
 * @returns An object containing query configurations and auto-generated React hooks for fetching data from the Terra service.
 */
export const terraService = AriApiQueryCreatorFetch({
  serviceURL: serviceurl,
  scopesObj: prismScopes,
  apiOptions: {
    reducerPath: 'terraApi',
    endpoints: (builder) => ({
      getEnterpriseAcres: builder.query<any, string[]>({
        query: (data) => ({
          url: '/GetEnterpriseAcres',
          method: 'POST',
          body: data
        })
      }),
      getFutureCrops: builder.query<FutureCropRow[], string[]>({
        query: (data) => ({
          url: '/GetFutureCrops',
          method: 'POST',
          body: data
        })
      }),
      getTerraCrops: builder.query<GetTerraCropsResponse, string[] | GetTerraCropsRequest>({
        query: (data) => ({
          url: '/GetTerraCrops',
          method: 'POST',
          body: normalizeTerraCropsArg(data)
        }),
        serializeQueryArgs: ({ queryArgs }) => {
          const norm = normalizeTerraCropsArg(queryArgs);
          return JSON.stringify(norm);
        }
      })
    })
  }
});

export const {
  useLazyGetEnterpriseAcresQuery,
  useLazyGetFutureCropsQuery,
  useGetFutureCropsQuery,
  useGetTerraCropsQuery,
  useLazyGetTerraCropsQuery
} = terraService.hooks;
